import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Role, User } from '../../types/users';
import axiosInstance from '../../api/axiosInstance';
import toast from 'react-hot-toast';
import {
  ID_AGENTE_VENTAS,
  ID_GERENTE_VENTAS,
  ID_LIDER_VENTAS,
  ID_ROL_LISTA_LIDERES_PARA_AGENTE,
} from '../../constants/roleIds';

interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user: User | null;
  roles: Role[];
}

const EditUserModal: React.FC<EditUserModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  user,
  roles
}) => {
  const [selectedRole, setSelectedRole] = useState<number>(user?.role_id || 0);
  const [supervisoresDisponibles, setSupervisoresDisponibles] = useState<User[]>([]);
  const [loadingSupervisores, setLoadingSupervisores] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedLider, setSelectedLider] = useState<string | number>(user?.lider_id || '');

  useEffect(() => {
    if (user) {
      setSelectedRole(user.role_id);
      setSelectedLider(user.lider_id || '');
    }
  }, [user]);

  useEffect(() => {
    if (selectedRole === ID_AGENTE_VENTAS) {
      setLoadingSupervisores(true);
      axiosInstance.get(`/users/list?role_id=${ID_ROL_LISTA_LIDERES_PARA_AGENTE}&limit=1000`)
        .then(res => {
          if (res.data.success) {
            setSupervisoresDisponibles(res.data.data || []);
          } else {
            setSupervisoresDisponibles([]);
          }
        })
        .catch(() => setSupervisoresDisponibles([]))
        .finally(() => setLoadingSupervisores(false));
    } else if (selectedRole === ID_LIDER_VENTAS) {
      setLoadingSupervisores(true);
      axiosInstance.get(`/users/list?role_id=${ID_GERENTE_VENTAS}&limit=1000`)
        .then(res => {
          if (res.data.success) {
            setSupervisoresDisponibles(res.data.data || []);
          } else {
            setSupervisoresDisponibles([]);
          }
        })
        .catch(() => setSupervisoresDisponibles([]))
        .finally(() => setLoadingSupervisores(false));
    } else {
      setSupervisoresDisponibles([]);
    }
  }, [selectedRole]);

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !loading) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, loading, onClose]);

  if (!isOpen || !user) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setLoading(true);
      const response = await axiosInstance.put(`/users/${user.id}`, {
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        role_id: selectedRole,
        agente_id: user.agente_id,
        lider_id:
          (selectedRole === ID_AGENTE_VENTAS || selectedRole === ID_LIDER_VENTAS) && selectedLider
            ? Number(selectedLider)
            : null
      });

      if (response.data.success) {
        toast.success('Usuario actualizado exitosamente', { position: 'top-center' });
        onSuccess();
        onClose();
      } else {
        toast.error(response.data.message || 'Error al actualizar el usuario', { position: 'top-center' });
      }
    } catch (error: any) {
      console.error('Error al actualizar usuario:', error);
      toast.error(error.response?.data?.message || 'Error al actualizar el usuario', { position: 'top-center' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !loading) onClose();
      }}
    >
      <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Editar Usuario</h2>
            <p className="mt-1 text-sm text-gray-500">Actualiza el rol y asignaciones del usuario</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 disabled:opacity-50"
            disabled={loading}
            aria-label="Cerrar modal de edición de usuario"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Nombre</label>
            <input
              type="text"
              value={user.first_name}
              disabled
              className="mt-1 block w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Apellido</label>
            <input
              type="text"
              value={user.last_name}
              disabled
              className="mt-1 block w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input
              type="email"
              value={user.email}
              disabled
              className="mt-1 block w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Rol</label>
            <select
              value={selectedRole}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                setSelectedRole(Number(e.target.value));
                setSelectedLider('');
              }}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              disabled={loading}
            >
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
          </div>

          {(selectedRole === ID_AGENTE_VENTAS || selectedRole === ID_LIDER_VENTAS) && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {selectedRole === ID_AGENTE_VENTAS ? 'Líder de ventas' : 'Gerente de ventas'}
              </label>
              <select
                value={selectedLider}
                onChange={e => setSelectedLider(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
                disabled={loadingSupervisores || loading}
              >
                <option value="">
                  {selectedRole === ID_AGENTE_VENTAS
                    ? 'Seleccione un líder de ventas'
                    : 'Seleccione un gerente de ventas'}
                </option>
                {supervisoresDisponibles.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.first_name} {u.last_name} ({u.email})
                  </option>
                ))}
              </select>
              {loadingSupervisores && (
                <div className="text-xs text-gray-400 mt-1">
                  {selectedRole === ID_AGENTE_VENTAS ? 'Cargando líderes...' : 'Cargando gerentes...'}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUserModal; 